<template>
  <cs-preloader-overlay :loading="loading">
    <div class="main-container">
      <div class="header-container">
        <div class="title-coinatiner">
          <capusta-title :title="displayTitle" history-mode />
          <help-link v-if="isShownHelpLink" :source="currentTab.helpLink" />
        </div>
        <div v-if="isShownTabs" class="tabs-container">
          <capusta-tab
            v-for="tab in filteredTabs"
            :key="`tab_${tab.key}`"
            :tab="tab"
            :active="activeTabKey === tab.key"
            @click="onChangeTab(tab.key)"
          />
        </div>
      </div>
      <div v-if="isShownForm" class="form-container">
        <component
          :is="componentsMap[activeTabKey]"
          :transaction="transaction"
          @showInfo="onShownInfo"
          @submit="onSubmit"
        />
      </div>
      <bill-information-modal v-model="isShownInfo" />
    </div>
  </cs-preloader-overlay>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { isBetaTest } from 'lib/system/env';

import logrocket from '@/services/logrocket';
import HTTPService from '@/services/Http';

import HelpLink from 'molecules/HelpLink';
import CapustaTab from 'modules/CapustaLayout/CapustaTab';
import CapustaTitle from 'molecules/CapustaTitle';

import BillInformationModal from '@/modules/BillInformationModal/App.vue';

import { INVOICING } from '@/router/const';

import {
  BILL,
  TIPS,
  TICKET,
  SUBSCRIPTION,
  TARIFF,
  ADVANCED_TARIFF,
  COMPONENTS_MAP
} from './const';

const DEFAULT_ACTIVE_TAB = BILL;

export default {
  name: 'Invoicing',
  components: {
    HelpLink,
    CapustaTab,
    CapustaTitle,
    BillInformationModal,
  },
  props: {
    transaction: Object,
  },
  async created() {
    this.loading = true;
    await this.fetchLimits();
    await this.fetchWriteOffOptions();
    this.initializeActiveTabKey();
    this.loading = false;
  },
  data() {
    return {
      loading: false,
      activeTabKey: null,
      isShownInfo: false,
      isBetaTest: isBetaTest(),
      componentsMap: COMPONENTS_MAP,

      navigationList: [
        {
          key: BILL,
          label: 'common.bill',
          icon: 'edit',
          help: true,
          helpLink: 'https://help.capusta.space/bill/fixedamount',
        },
        {
          key: TIPS,
          label: 'navigation.bill.tips',
          icon: 'star',
          help: true,
          helpLink: 'https://help.capusta.space/payments/arbitraryamount',
        },
        {
          key: TICKET,
          label: 'navigation.bill.ticket',
          icon: 'promocode',
          help: true,
          helpLink: 'https://help.capusta.space/ticket_qr',
        },
        {
          key: SUBSCRIPTION,
          label: 'common.subscription',
          icon: 'calendar-adv',
          help: true,
          helpLink: 'https://help.capusta.space/bill/subscription',
        },
        {
          key: TARIFF,
          label: 'common.tariff',
          icon: 'tariff',
          help: true,
          helpLink: 'https://help.capusta.space/bill/tariff',
        },
        {
          key: ADVANCED_TARIFF,
          label: 'custom.variant',
          icon: 'maker',
          help: true,
          helpLink: 'https://help.capusta.space/bill/youroptions',
        },
      ],

      invoicingTitleList: [
        {
          key: BILL,
          label: this.$t('common.bill'),
        },
        {
          key: SUBSCRIPTION,
          label: this.$t('common.subscription'),
        },
        {
          key: TARIFF,
          label: this.$t('common.tariff'),
        },
        {
          key: ADVANCED_TARIFF,
          label: this.$t('custom.variant'),
        },
      ],
    };
  },
  computed: {
    isShownTabs() {
      if (this.isDesktop) {
        return true;
      }

      return this.$validate.isNull(this.activeTabKey);
    },
    isShownForm() {
      if (this.isDesktop) {
        return true;
      }

      return this.$validate.isNotNull(this.activeTabKey);
    },
    title() {
      if (this.isDesktop) {
        return;
      }

      return this.isShownForm ? this.$t('make.bill') : this.$t('common.create');
    },
    displayTitle() {
      const item = this.invoicingTitleList.find(item => item.key === this.activeTabKey);

      return item?.label || this.title;
    },
    filteredTabs() {
      if (this.periodicRecurrentPaymentPermission) {
        return this.navigationList;
      }

      return this.navigationList.filter(item => item.key !== SUBSCRIPTION);
    },
    currentTab() {
      return this.navigationList.find(navigation => navigation.key === this.activeTabKey) || null;
    },
    isShownHelpLink() {
      if (this.isDesktop) {
        return false;
      }

      if (this.$validate.isNull(this.currentTab)) {
        return false;
      }

      return this.currentTab.help;
    },
    ...mapGetters(['isDesktop', 'periodicRecurrentPaymentPermission']),
  },
  watch: {
    $route: {
      handler(val) {
        const { hash } = val;

        if (this.isDesktop) {
          this.initializeActiveTabKey();
          return;
        }

        if (this.$validate.isEmptyString(hash)) {
          this.activeTabKey = null;
        }
      },
    },
  },
  methods: {
    initializeActiveTabKey() {
      if (this.$route.hash) {
        const activeTabKey = this.$route.hash.replace('#', '');

        if (!this.periodicRecurrentPaymentPermission && activeTabKey === SUBSCRIPTION) {
          this.$router.push({ name: INVOICING });
          return;
        }

        this.activeTabKey = activeTabKey;
        return;
      }

      if (this.$validate.isNotUndefined(this.transaction)) {
        this.activeTabKey = this.transaction.type.toLowerCase();
        return;
      }

      if (!this.isDesktop) {
        return;
      }

      this.activeTabKey = DEFAULT_ACTIVE_TAB;
    },
    onShownInfo() {
      this.isShownInfo = true;
    },
    onChangeTab(tab) {
      this.activeTabKey = tab;
    },
    async onSubmit(payload) {
      this.loading = true;
      try {
        const { data } = await HTTPService.createBill(payload.bill);

        this.$alert({
          type: 'success',
          text: 'bill.created',
        });

        this.$router.push({
          name: 'InvoicingResult',
          params: {
            billParams: Object.assign(payload.bill, data),
            showQr: payload.qrCode,
          },
        });
      } catch (error) {
        logrocket.logger(error);

        const { status } = error;

        if (status === 403) {
          this.$alert({
            type: 'warning',
            text: 'limits.sum.error',
          });
        } else {
          this.$alert({
            type: 'warning',
            text: 'server.error',
          });
        }
      }
      this.loading = false;
    },
    ...mapActions('bills', ['fetchLimits', 'fetchWriteOffOptions']),
  },
};
</script>

<style lang="sass" scoped>
  .main-container
    width: 100%
    max-width: 500px
    margin: 0 auto
    padding: 20px 0 60px

    +respond-to('medium')
      max-width: 620px
      padding: 60px 0

  .header-container
    display: flex
    flex-direction: column
    justify-content: center
    margin-bottom: 20px
    padding: 0 20px

    +respond-to('small')
      padding: 0

    +respond-to('medium')
      flex-direction: row

  .title-coinatiner
    display: flex
    align-items: center
    justify-content: space-between

  .tabs-container
    display: grid
    grid-gap: 20px
    grid-template-rows: 1fr 1fr
    grid-template-columns: 1fr 1fr
    margin-top: 20px

    +respond-to('medium')
      display: flex
      margin: 0

  .form-container
    width: 100%
    max-width: 500px
    position: relative
    margin: 0 auto
</style>
